import React, { ReactElement, useState } from 'react';
import styled from 'styled-components';
//
import BasicLayout from 'components/layout/basic-layout';
import { ActionBar, Container, ShareButton, ToggleButtonContainer } from 'pages/compare/components';
import ToggleButton from 'components/buttons/toggle';
import { EForecastEvaluationType } from 'store/deal-evaluation/types';
import UnitToggle from 'components/unit-toggle';
import CostFormulaToggle from 'components/cost-formula-toggle';
import CompareDefaultView from 'pages/compare/default-view';
import CompareBoxPlot from 'pages/compare/box-plot';
import BoxPlotIcon from 'components/icons/box-plot';
import CompareTabIcon from 'components/icons/compare-tab';
import colors from 'theme/colors';
import UrlShare from 'components/url-share';
import messages from 'constants/messages';
import ModalBasic from 'components/modal-provider/modal-basic';
import { ModalButton } from 'components/modal-provider/modal';
import labels from 'constants/labels';
import MyBookmark from 'pages/bookmark/bookmark-common';

const ActionBarRight = styled.div`
  display: flex;
  align-items: center;
`;

const IconContainer = styled.div`
  display: flex;
  margin-right: 16px;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  margin-left: 8px;
  display: flex;

  &:hover {
    cursor: pointer;
  }
  &:focus,
  &:active {
    outline: none;
  }
`;

export enum EComparePageView {
  STANDARD = 'standard',
  LINE_GRAPH = 'lineGraph',
  BOX_PLOT = 'boxPlot',
}

interface IComparePageProps {
  evaluateDeals: () => void;
  selectedForecastType: EForecastEvaluationType;
  setForecastType: (forecastType: EForecastEvaluationType) => void;
  clearModalContent: () => void;
  setModalContent: (content: ReactElement) => void;
  compareDealIds: string[];
}

export default ({
  evaluateDeals,
  selectedForecastType,
  setForecastType,
  clearModalContent,
  setModalContent,
  compareDealIds,
}: IComparePageProps) => {
  const [view, setView] = useState(EComparePageView.STANDARD);

  const handleIhsForecastClick = () => {
    if (selectedForecastType === EForecastEvaluationType.IHS) {
      return;
    }
    setForecastType(EForecastEvaluationType.IHS);
    evaluateDeals();
  };

  const handleShellForecastClick = () => {
    if (selectedForecastType === EForecastEvaluationType.SHELL) {
      return;
    }
    setForecastType(EForecastEvaluationType.SHELL);
    evaluateDeals();
  };

  const handleShare = () => {
    const modalContent = (
      <>
        <p>{messages.info.shareDeal}</p>
        <UrlShare url={window.location.href} />
        <br />
      </>
    );
    const modalButton = <ModalButton onClick={clearModalContent}>{labels.button.close}</ModalButton>;
    const modal = (
      <ModalBasic
        title={labels.titles.modals.shareDeal}
        content={modalContent}
        onClose={clearModalContent}
        buttons={modalButton}
      />
    );
    setModalContent(modal);
  };

  return (
    <BasicLayout>
      <Container>
        <ActionBar>
          <ToggleButtonContainer>
            <ToggleButton
              isPrimaryOptionActive={selectedForecastType === EForecastEvaluationType.IHS}
              primaryOptionText="IHS Forecast"
              secondaryOptionText="Shell Forecast"
              onPrimaryOptionClick={handleIhsForecastClick}
              onSecondaryOptionClick={handleShellForecastClick}
            />
            <UnitToggle showTitle={false} />
            <CostFormulaToggle showTitle={false} />
          </ToggleButtonContainer>

          <ActionBarRight>
            <IconContainer>
              <IconButton>
                <MyBookmark bookmarkModule="Deal Comparison" bookmarkLink={window.location.pathname} />
              </IconButton>
              <IconButton>
                <CompareTabIcon
                  onClick={() => setView(EComparePageView.STANDARD)}
                  fill={view === EComparePageView.STANDARD ? colors.primary : colors.globalIcon}
                />
              </IconButton>
              <IconButton>
                <BoxPlotIcon
                  onClick={() => setView(EComparePageView.BOX_PLOT)}
                  fill={view === EComparePageView.BOX_PLOT ? colors.primary : colors.globalIcon}
                />
              </IconButton>
            </IconContainer>
            <ShareButton onClick={evaluateDeals}>Refresh</ShareButton>
            <ShareButton disabled={compareDealIds.length === 0} onClick={handleShare}>
              Share
            </ShareButton>
          </ActionBarRight>
        </ActionBar>

        {view === EComparePageView.STANDARD ? <CompareDefaultView /> : <CompareBoxPlot />}
      </Container>
    </BasicLayout>
  );
};
